'use client';

import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { DataTestConfig, NEWSLETTER_MODAL_ID, Position } from '@headless-workspace/config';
import { SideMenuModal, useModalActions } from '@headless-workspace/core-ui';
import {
    DataLayerEvent,
    DataLayerEventLabel,
    trackNewsletterClick,
    useDataLayer,
} from '@headless-workspace/domain/common/client';
import { UserInfo } from '@headless-workspace/domain/common/server';
import {
    GlowButton,
    GlowInput,
    GlowTextBodySmall,
    ModalCommonClasses,
    PropsWithClassName,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { StringHelpers } from '@headless-workspace/utils';
import { NewsletterConfirmationModal } from '../../../../server';
import { NewsletterModal } from '../../modal';
import { useTriggerNewsletterSubscription } from './useTriggerNewsletterSubscription';

const EMAIL_FORM_ID = 'emailFormId';
const EMAIL_MAX_LENGTH = 70;

type NewsletterFormProps = PropsWithClassName &
    PropsWithLocale & {
        defaultEmail?: string;
        onSubscribe: () => void;
        onUserSubmit: (status: boolean) => void;
        userInfo?: UserInfo;
        newsletterConfirmationPopup: NewsletterConfirmationModal;
    };

export const NewsletterForm: Component<NewsletterFormProps> = ({
    className,
    defaultEmail = '',
    locale,
    onSubscribe,
    onUserSubmit,
    userInfo,
    newsletterConfirmationPopup,
}) => {
    const t = useTranslations('Discover.Home.newsletter');

    const { addAndOpen: addAndOpenModal } = useModalActions();

    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [email, setEmail] = useState<string>(defaultEmail);

    useEffect(() => {
        setEmail(defaultEmail);
    }, [defaultEmail]);

    const { trackEvent } = useDataLayer();
    const trackEventClick = useCallback(
        (label: DataLayerEventLabel) => {
            trackEvent(DataLayerEvent.Click, trackNewsletterClick(label, userInfo));
        },
        [trackEvent, userInfo],
    );

    const triggerSubscribe = useTriggerNewsletterSubscription(email, onSubscribe, trackEventClick);

    const onSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (email === '') {
                setErrorMessage(t('label.errorEmpty'));
            } else if (StringHelpers.validateEmail(email)) {
                setErrorMessage(undefined);
                trackEventClick(DataLayerEventLabel.Init);

                addAndOpenModal(
                    NEWSLETTER_MODAL_ID,
                    SideMenuModal({
                        side: Position.None,
                        map: {
                            default: (
                                <NewsletterModal
                                    locale={locale}
                                    email={email}
                                    onSubscribe={triggerSubscribe}
                                    onUserSubmit={onUserSubmit}
                                    newsletterConfirmationPopup={newsletterConfirmationPopup}
                                />
                            ),
                        },
                        className: ModalCommonClasses,
                    }),
                );
            } else {
                setErrorMessage(t('label.errorInvalid'));
            }
        },
        [
            addAndOpenModal,
            email,
            locale,
            newsletterConfirmationPopup,
            onUserSubmit,
            t,
            trackEventClick,
            triggerSubscribe,
        ],
    );

    return (
        // Form validation should be deactivated, as it is custom handled by the 'onSubmit' method
        <form className={clsx(className)} onSubmit={onSubmit} id={EMAIL_FORM_ID} noValidate>
            <GlowTextBodySmall
                htmlFor={'newsletterEmail'}
                TagName={'label'}
                text={t('label.email')}
                className={'line-clamp-2'}
            />
            <div className={'flex flex-col tablet:flex-row gap-0.5 items-start'}>
                <GlowInput
                    fieldId={'newsletterEmail'}
                    defaultValue={email}
                    helperText={t('label.hint')}
                    errorText={errorMessage}
                    type={'email'}
                    inputMode={'email'}
                    name={'newsletterEmail'}
                    id={'newsletterEmail'}
                    enterKeyHint={'send'}
                    maxLength={EMAIL_MAX_LENGTH}
                    aria-label={t('ariaLabel.input')}
                    aria-autocomplete={'none'}
                    dataTestId={DataTestConfig.testIds.newsletterMailInput}
                    onChange={(e) => setEmail(e.target.value)}
                    displayMaxLength={false}
                    className={'w-full'}
                />
                <GlowButton
                    dataTestId={DataTestConfig.testIds.newsletterSubscriptionButton}
                    label={t('action.subscribe')}
                    variant={'primary'}
                    formId={EMAIL_FORM_ID}
                    width={'responsive'}
                />
            </div>
        </form>
    );
};
